// CDV-017..CDV-027: render a composed view as an ordered list of blocks.
// Each block knows how to render itself; this component is just the
// switch + the per-block scope cascade for embedded data blocks.

import { useChatPageContext } from '@/components/chat/useChatPageContext'
import { AnalysesBody } from '@/components/sala/AnalysesBody'
import { EventsByHourBody } from '@/components/sala/EventsByHourBody'
import { PhotosBody } from '@/components/sala/PhotosBody'
import { TimelineBody } from '@/components/sala/TimelineBody'
import { ConfirmCardBlockView } from '@/components/sala/blocks/ConfirmCardBlockView'
import { FormBlockView } from '@/components/sala/blocks/FormBlockView'
import { KpiBlockView } from '@/components/sala/blocks/KpiBlockView'
import { ListBlockView } from '@/components/sala/blocks/ListBlockView'
import { MarkdownBlockView } from '@/components/sala/blocks/MarkdownBlockView'
import { TableBlockView } from '@/components/sala/blocks/TableBlockView'
import {
  analysesIntentFromBlock,
  eventsByHourIntentFromBlock,
  photosIntentFromBlock,
  timelineIntentFromBlock,
} from '@/lib/composedScope'
import type { Block, ComposedIntent } from '@/lib/viewIntent'
import { t } from '@/lib/i18n'

interface Props { //@usedby:Sala.tsx
  intent: ComposedIntent
}

export function ComposedBody({ intent }: Props) {
  if (intent.blocks.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-foreground-muted">
        {t('This view has no content.')}
      </div>
    )
  }
  // CDV-022: consecutive `kpi` blocks share a horizontal grid container.
  // Every other block type renders full-width top-to-bottom.
  const groups = groupConsecutiveKpis(intent.blocks)
  return (
    <div className="space-y-4">
      {groups.map((group, gi) =>
        group.kind === 'kpi-row' ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: groups are positional
          <div
            key={gi}
            data-testid="kpi-row"
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3"
          >
            {group.blocks.map((block, bi) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: blocks are positional
              <BlockRenderer key={bi} block={block} composed={intent} />
            ))}
          </div>
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: groups are positional
          <BlockRenderer key={gi} block={group.block} composed={intent} />
        ),
      )}
    </div>
  )
}

type BlockGroup =
  | { kind: 'kpi-row'; blocks: Extract<Block, { type: 'kpi' }>[] }
  | { kind: 'single'; block: Block }

function groupConsecutiveKpis(blocks: Block[]): BlockGroup[] { //@usedby:ComposedBody.test.tsx
  const out: BlockGroup[] = []
  let i = 0
  while (i < blocks.length) {
    const block = blocks[i]!
    if (block.type === 'kpi') {
      const run: Extract<Block, { type: 'kpi' }>[] = []
      while (i < blocks.length && blocks[i]!.type === 'kpi') {
        run.push(blocks[i] as Extract<Block, { type: 'kpi' }>)
        i++
      }
      out.push({ kind: 'kpi-row', blocks: run })
    } else {
      out.push({ kind: 'single', block })
      i++
    }
  }
  return out
}

function BlockRenderer({ block, composed }: { block: Block; composed: ComposedIntent }) {
  const ctx = useChatPageContext()
  switch (block.type) {
    case 'markdown':
      return <MarkdownBlockView text={block.text} />
    case 'table':
      return <TableBlockView columns={block.columns} rows={block.rows} caption={block.caption} />
    case 'list':
      return <ListBlockView items={block.items} ordered={block.ordered} />
    case 'kpi':
      return (
        <KpiBlockView
          label={block.label}
          value={block.value}
          unit={block.unit}
          trend={block.trend}
          caption={block.caption}
        />
      )
    case 'form':
      return (
        <FormBlockView
          id={block.id}
          title={block.title}
          description={block.description}
          submit_label={block.submit_label}
          fields={block.fields}
        />
      )
    case 'confirm_card':
      return (
        <ConfirmCardBlockView
          id={block.id}
          text={block.text}
          actions={block.actions}
          payload={block.payload}
        />
      )
    case 'photos': {
      const resolved = photosIntentFromBlock(block, composed, ctx)
      if (!resolved) return <BlockErrorNote />
      return <PhotosBody intent={resolved} />
    }
    case 'analyses': {
      const resolved = analysesIntentFromBlock(block, composed, ctx)
      if (!resolved) return <BlockErrorNote />
      return <AnalysesBody intent={resolved} />
    }
    case 'timeline': {
      const resolved = timelineIntentFromBlock(block, composed, ctx)
      if (!resolved) return <BlockErrorNote />
      return <TimelineBody intent={resolved} />
    }
    case 'events_by_hour': {
      const resolved = eventsByHourIntentFromBlock(block, composed, ctx)
      if (!resolved) return <BlockErrorNote />
      return <EventsByHourBody intent={resolved} />
    }
  }
}

function BlockErrorNote() {
  return (
    <div className="rounded-lg border border-dashed border-border bg-card/50 p-4 text-sm text-foreground-muted italic">
      {t("This view section couldn't be displayed.")}
    </div>
  )
}
